import axios from 'axios';
import { InteractionRequiredAuthError, InteractionStatus, PublicClientApplication } from '@azure/msal-browser';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { getAuthority, loginRequest, msalConfig } from '../authConfig';
import configData from '../config.json';
import { appInsights } from '../services/ApplicationInsightsService';
import { ACCESS_TOKEN_NAME } from '../utils/enums';
// eslint-disable-next-line import/no-cycle
import { isTokenExpired } from '../utils/helpers';
import { logAppError, logAuthStep } from '../utils/logActions';

const searchParams = new URLSearchParams(window.location.search);
const idTokenHint = searchParams.get('id_token_hint');
const authority = getAuthority(idTokenHint);
const msalInstance = new PublicClientApplication(msalConfig(authority));

export const API_URL = `${configData.REACT_APP_BACKEND_URL}/api`;
export const TNE_API_URL = `${configData.REACT_APP_TANDE_URL}/api`;

function isInteractionInProgress() {
  return localStorage.getItem("msal.interaction.status") === InteractionStatus.Startup ||
         localStorage.getItem("msal.interaction.status") === InteractionStatus.HandleRedirect;
}

async function refreshAccessToken() {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) return null;

  const cachedToken = localStorage.getItem(ACCESS_TOKEN_NAME);
  if (cachedToken && !isTokenExpired(cachedToken)) {
    return cachedToken;
  }

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: account,
    });
    localStorage.setItem(ACCESS_TOKEN_NAME, tokenResponse.accessToken);
    return tokenResponse.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError && !isInteractionInProgress()) {
      return await msalInstance.loginRedirect({
        scopes: loginRequest.scopes
      });
    }
    throw error;
  }
}


async function handleReAuthentication(error) {
  if (error instanceof InteractionRequiredAuthError) {
    appInsights.trackException({
      name: 'Re-authentication required, redirecting to login',
      error: error,
    });
    if (!isInteractionInProgress()) {
      return msalInstance.loginRedirect({
        ...loginRequest,
        state: JSON.stringify({ navigate: window.location.pathname }),
      }).catch(e => {
        appInsights.trackException({
          name: 'Error during login redirect',
          error: e,
        });
        return Promise.reject(e);
      });
    }
  }
}


async function handle401Error(originalRequest) {
  try {
    const { accessToken } = await refreshAccessToken();
    const isQuickApplyEmbedded = () => window.location.pathname.startsWith('/quickapply-embedded');
    if (!accessToken) {
      if (!isInteractionInProgress() && !isQuickApplyEmbedded()) {
        return handleReAuthentication();
      }
    }
    localStorage.setItem(ACCESS_TOKEN_NAME, accessToken);
    originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
    return axios(originalRequest); // Retry original request
  } catch (err) {
    console.log('err: ', err)
    // await handleReAuthentication(err);
    return Promise.reject(err);
  }
}

function handle403Error(error) {
  error.response.data = { error: "Sorry. Not enough permissions." };
  appInsights.trackException({
    name: 'Error Permission Denied',
    error: 'Sorry. Not enough permissions',
    statusCode: error.response.status,
  });
  return Promise.reject(error);
}

const createAxiosClient = (baseURL) => {
  const client = axios.create({ baseURL, withCredentials: true });

  client.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(ACCESS_TOKEN_NAME);
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json;charset=utf-8';
      }

      config.headers['Access-Control-Allow-Origin'] = '*';
      config.headers['Access-Control-Allow-Credentials'] = 'true';
      config.headers['Access-Control-Allow-Methods'] = 'GET,PUT,PATCH,POST,DELETE,HEAD,OPTIONS';

      return config;
    },
    (error) => {
      logAppError('API_Request_Error_in_Request', { error });
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401) {
        msalInstance.logoutRedirect();
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const axiosClient = createAxiosClient(API_URL);
export const tneAxiosClient = createAxiosClient(TNE_API_URL);

export default axiosClient;